import {Heading, Text} from '@primer/react-brand'
import {CheckIcon, ChevronDownIcon, HorizontalRuleIcon} from '@primer/octicons-react'
import {useMemo} from 'react'
import {clsx} from 'clsx'

import Accordion from './../../Accordion/Accordion'
import useAccordionStates from './../../Accordion/hooks/useAccordionStates'
import type {FeaturesTableProps} from './../FeaturesTable.types'

import FeaturesTableStyles from './../FeaturesTable.module.css'
import Styles from './FeaturesTableMobile.module.css'

export type FeaturesTableMobileProps = FeaturesTableProps

const defaultProps: Partial<FeaturesTableMobileProps> = {}

const FeaturesTableMobile: React.FC<FeaturesTableMobileProps> = props => {
  const initializedProps = {...defaultProps, ...props}
  const {title, columns, rows, aria} = initializedProps

  const {itemStates, updateItem} = useAccordionStates({items: rows})

  const featureItems = useMemo(() => {
    return rows.map((row, index) => {
      const isExpanded = !!itemStates[index]

      const headingDOM = (
        <div
          className={clsx(
            Styles['FeaturesTableMobile__featureItemTitleBlock'],
            isExpanded && Styles['FeaturesTableMobile__featureItemTitleBlock--expanded'],
          )}
        >
          <Heading size="subhead-large" as="h4" className={Styles['FeaturesTableMobile__featureItemTitleBlock-title']}>
            {row.title}
          </Heading>
          <ChevronDownIcon size={24} className={Styles['FeaturesTableMobile__featureItemTitleBlock-chevron']} />
        </div>
      )

      const contentDOM = (
        <>
          <Text as="p" className={Styles['FeaturesTableMobile__featureItemTitleBlock-description']} variant="muted">
            {row.description}
          </Text>

          {columns.map((tier, tierIndex) => {
            const content = row.content[tierIndex]
            if (!content) return null

            const {customText: availabilityText, availability: isAvailable} = content

            return (
              <div key={tier.id} className={Styles['FeaturesTableMobile__featureItemTier']}>
                <Text className={Styles['FeaturesTableMobile__featureItemTier-label']} weight="semibold">
                  {tier.title}
                </Text>

                <div
                  className={clsx(
                    Styles['FeaturesTableMobile__featureItemTier-availability'],
                    availabilityText
                      ? false
                      : isAvailable
                        ? Styles['FeaturesTableMobile__featureItemTier-availability--green']
                        : Styles['FeaturesTableMobile__featureItemTier-availability--grey'],
                  )}
                >
                  {availabilityText ? (
                    availabilityText
                  ) : isAvailable ? (
                    <>
                      <CheckIcon size={24} />
                      <span className={FeaturesTableStyles['FeaturesTable__srOnly']}>{aria.included}</span>
                    </>
                  ) : (
                    <>
                      <HorizontalRuleIcon size={24} />
                      <span className={FeaturesTableStyles['FeaturesTable__srOnly']}>{aria.notIncluded}</span>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </>
      )

      return {
        heading: headingDOM,
        content: contentDOM,
        isExpanded,
      }
    })
  }, [rows, columns, itemStates, aria])

  return (
    <div className={Styles['FeaturesTableMobile']}>
      <Heading size="5" as="h3" className={Styles['FeaturesTableMobile__title']}>
        {title}
      </Heading>

      <Accordion
        items={featureItems}
        itemClassName={Styles['FeaturesTableMobile__featureItem']}
        onItemToggle={updateItem}
      />
    </div>
  )
}

export default FeaturesTableMobile
